import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Users, Shield, MessageCircle, Sparkles, X, ChevronRight, Search, Wand2 } from "lucide-react";
import { Button } from "./ui/button";

const ONBOARDING_KEY = "loveconnect_onboarding_completed";

const ONBOARDING_STEPS = [
  {
    icon: Heart,
    title: { fr: "Bienvenue sur Love Connect +", en: "Welcome to Love Connect +" },
    description: { fr: "Trouvez l'amour parmi des milliers de profils vérifiés près de chez vous", en: "Find love among thousands of verified profiles near you" },
    color: "bg-gradient-to-br from-pink-500 to-rose-600"
  },
  {
    icon: Users,
    title: { fr: "Découvrez des profils", en: "Discover profiles" },
    description: { fr: "Swipez à droite pour liker, à gauche pour passer. C'est simple et intuitif !", en: "Swipe right to like, left to pass. It's simple and intuitive!" },
    color: "bg-gradient-to-br from-violet-500 to-purple-600"
  },
  {
    icon: Wand2,
    title: { fr: "Recherche IA intelligente", en: "Smart AI Search" },
    description: { fr: "Décrivez votre partenaire idéal en langage naturel ! Notre IA trouve les profils qui correspondent à vos critères. Ex: \"Une femme sportive de 25-30 ans à Lyon\"", en: "Describe your ideal partner in natural language! Our AI finds profiles matching your criteria. E.g: \"A sporty woman aged 25-30 in Lyon\"" },
    color: "bg-gradient-to-br from-indigo-500 to-purple-600"
  },
  {
    icon: Shield,
    title: { fr: "Profils vérifiés", en: "Verified profiles" },
    description: { fr: "Recherchez le badge vert pour identifier les profils authentiques et vérifiés", en: "Look for the green badge to identify authentic and verified profiles" },
    color: "bg-gradient-to-br from-green-500 to-emerald-600"
  },
  {
    icon: MessageCircle,
    title: { fr: "Messagerie gratuite", en: "Free messaging" },
    description: { fr: "Envoyez jusqu'à 3 messages gratuits par jour. Messages gratuits entre matchs !", en: "Send up to 3 free messages per day. Free messaging between matches!" },
    color: "bg-gradient-to-br from-blue-500 to-cyan-600"
  },
  {
    icon: Sparkles,
    title: { fr: "Super Likes", en: "Super Likes" },
    description: { fr: "Démarquez-vous avec les Super Likes et augmentez vos chances de match !", en: "Stand out with Super Likes and increase your chances of matching!" },
    color: "bg-gradient-to-br from-amber-500 to-orange-600"
  }
];

export default function Onboarding({ language = "fr", onComplete }) {
  // Check localStorage directly - if completed, don't render at all
  const isCompleted = localStorage.getItem(ONBOARDING_KEY) === "true";
  
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(!isCompleted);

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setVisible(false);
    if (onComplete) onComplete();
  };

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  // Don't render if completed or hidden
  if (!visible) return null;

  const step = ONBOARDING_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-4"
      >
        {/* Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 text-white/70 hover:text-white transition-colors z-10"
        >
          {language === 'fr' ? 'Passer' : 'Skip'}
          <X className="w-4 h-4" />
        </button>

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="max-w-md w-full text-center"
        >
          {/* Icon */}
          <div className={`w-24 h-24 rounded-3xl ${step.color} flex items-center justify-center mx-auto mb-8 shadow-2xl`}>
            <Icon className="w-12 h-12 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-4">
            {step.title[language]}
          </h2>

          {/* Description */}
          <p className="text-white/70 mb-8 px-4">
            {step.description[language]}
          </p>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-8">
            {ONBOARDING_STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep ? 'bg-primary w-6' : 'bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Next/Finish Button */}
          <Button
            onClick={handleNext}
            size="lg"
            className="w-full max-w-xs mx-auto bg-primary hover:bg-primary/90 rounded-full font-bold"
          >
            {currentStep === ONBOARDING_STEPS.length - 1 ? (
              language === 'fr' ? "C'est parti !" : "Let's go!"
            ) : (
              <>
                {language === 'fr' ? 'Suivant' : 'Next'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
